import { useEffect, useRef, useState, type RefObject } from 'react';

const TRIGGER = 64;
const MAX = 90;

/**
 * «Потяни вниз, чтобы обновить» для прокручиваемого блока. Срабатывает только когда блок
 * прокручен до самого верха; тянуть вниз с середины списка просто прокручивает его.
 * Возвращает высоту «ушка» индикатора и признак идущего обновления.
 */
export function usePullToRefresh(ref: RefObject<HTMLElement | null>, onRefresh: () => Promise<unknown>) {
  const [pull, setPull] = useState(0);
  const [busy, setBusy] = useState(false);
  const start = useRef<number | null>(null);
  const pullRef = useRef(0);
  const refresh = useRef(onRefresh);
  refresh.current = onRefresh;

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const onStart = (e: TouchEvent) => {
      start.current = el.scrollTop <= 0 ? e.touches[0].clientY : null;
    };
    const onMove = (e: TouchEvent) => {
      if (start.current === null) return;
      const dy = e.touches[0].clientY - start.current;
      if (dy <= 0 || el.scrollTop > 0) { pullRef.current = 0; setPull(0); return; }
      pullRef.current = Math.min(MAX, dy * 0.5);
      setPull(pullRef.current);
    };
    const onEnd = () => {
      // Палец прошёл TRIGGER точек (индикатор тянется вдвое медленнее пальца).
      const reached = pullRef.current >= TRIGGER / 2;
      start.current = null;
      pullRef.current = 0;
      setPull(0);
      if (!reached) return;
      setBusy(true);
      void refresh.current().finally(() => setBusy(false));
    };
    el.addEventListener('touchstart', onStart, { passive: true });
    el.addEventListener('touchmove', onMove, { passive: true });
    el.addEventListener('touchend', onEnd);
    el.addEventListener('touchcancel', onEnd);
    return () => {
      el.removeEventListener('touchstart', onStart);
      el.removeEventListener('touchmove', onMove);
      el.removeEventListener('touchend', onEnd);
      el.removeEventListener('touchcancel', onEnd);
    };
  }, [ref]);

  return { height: busy ? 44 : pull, busy, pulling: pull > 0 };
}
